// D46-D48: the arithmetic behind the repay panel and the repay form. Pure functions, exact decimals only
// (src/lib/decimal.ts, never a float). The server stays authoritative: this is the owner's live preview and
// the client-side validation that spares a round trip.
import { addDecimal, compareDecimal, isDecimalString, mulDecimal, subDecimal } from './decimal';

/** An open borrow as the panel needs it (the outstanding is net of every OTHER repay's links). */
export interface PanelLoan {
  id: string;
  occurred_at: string;
  outstanding: string;
  price_level: string | null;
  price_level_reason: string | null;
  price_level_source: string | null;
  price_kind: 'coin' | 'stablecoin';
}

export interface RowInput {
  on: boolean;
  qty: string;
}

export interface ResultValue {
  value: string | null;
  reason: string | null;
}

export type RowError = 'invalid' | 'exceeds_outstanding' | null;

export interface PlanLine {
  loan: PanelLoan;
  on: boolean;
  qty: string;
  error: RowError;
  /** Outstanding after this link; null when the quantity is not usable. */
  after: string | null;
  /** The realized result of this link (USD, positive = gain); null when the row is off. */
  result: ResultValue | null;
}

export interface Plan {
  lines: PlanLine[];
  linkedTotal: string;
  /** Principal part minus linked; null when the principal is unknown. */
  unlinked: string | null;
  over: boolean;
  /** Sum of the rows' results; null value + reason as soon as one linked row is unknown (never a partial sum). */
  total: ResultValue;
  /** Every linked row is valid and the total does not exceed the principal. */
  valid: boolean;
  links: { borrow_event_id: string; quantity: string }[];
}

export interface ServerResult {
  proposed: string;
  value: string | null;
  reason: string | null;
}

const positive = (s: string): boolean => isDecimalString(s) && compareDecimal(s, '0') > 0;

/** (borrow level - repay level) x quantity, USD; POSITIVE = gain. Unknown stays unknown with its reason. */
export function linkResult(loan: PanelLoan, qty: string, repay: { level: string | null; reason: string | null }, server?: ServerResult): ResultValue {
  if (server && isDecimalString(qty) && compareDecimal(server.proposed, qty) === 0) return { value: server.value, reason: server.reason };
  // A stablecoin debt is valued 1:1 at both ends (hypothesis): its realized result is exactly 0, like the server's.
  if (loan.price_kind === 'stablecoin') return { value: '0', reason: null };
  if (loan.price_level === null) return { value: null, reason: loan.price_level_reason ?? 'borrow_price_level_unavailable' };
  if (repay.level === null) return { value: null, reason: repay.reason ?? 'repay_price_level_unavailable' };
  if (!positive(qty)) return { value: null, reason: 'no_links' };
  return { value: mulDecimal(subDecimal(loan.price_level, repay.level), qty), reason: null };
}

export function planLinks(
  loans: readonly PanelLoan[],
  rows: Readonly<Record<string, RowInput>>,
  principal: string | null,
  repay: { level: string | null; reason: string | null },
  server: Readonly<Record<string, ServerResult>> = {},
): Plan {
  let linkedTotal = '0';
  let allValid = true;
  const lines: PlanLine[] = loans.map((loan) => {
    const row = rows[loan.id] ?? { on: false, qty: '' };
    const qty = row.qty.trim().replace(',', '.');
    if (!row.on) return { loan, on: false, qty, error: null, after: null, result: null };
    let error: RowError = null;
    if (!positive(qty)) error = 'invalid';
    else if (compareDecimal(qty, loan.outstanding) > 0) error = 'exceeds_outstanding';
    if (error) allValid = false;
    else linkedTotal = addDecimal(linkedTotal, qty);
    return {
      loan, on: true, qty, error, after: error === null ? subDecimal(loan.outstanding, qty) : null,
      result: error === null ? linkResult(loan, qty, repay, server[loan.id]) : { value: null, reason: 'no_links' },
    };
  });
  const on = lines.filter((l) => l.on && l.error === null);
  let total: ResultValue;
  if (on.length === 0) total = { value: null, reason: 'no_links' };
  else {
    const unknown = on.find((l) => l.result?.value === null || l.result === null);
    if (unknown) total = { value: null, reason: unknown.result?.reason ?? 'no_links' };
    else total = { value: on.reduce((s, l) => addDecimal(s, l.result?.value ?? '0'), '0'), reason: null };
  }
  const over = principal !== null && compareDecimal(linkedTotal, principal) > 0;
  return {
    lines, linkedTotal, unlinked: principal === null ? null : over ? '0' : subDecimal(principal, linkedTotal), over, total,
    valid: allValid && !over,
    links: on.map((l) => ({ borrow_event_id: l.loan.id, quantity: l.qty })),
  };
}

/** The principal part of a repay (quantity - interest); null when either is not a usable decimal or the interest is out of range. */
export function principalPart(qty: string, interest: string): string | null {
  if (!positive(qty)) return null;
  const i = interest.trim() === '' ? '0' : interest.trim().replace(',', '.');
  if (!isDecimalString(i) || compareDecimal(i, '0') < 0 || compareDecimal(i, qty) > 0) return null;
  return subDecimal(qty, i);
}

/** "Ebből kamat" validation: empty = 0; a decimal >= 0 and <= the quantity. */
export function interestError(qty: string, interest: string): null | 'invalid' | 'exceeds_quantity' {
  const i = interest.trim().replace(',', '.');
  if (i === '') return null;
  if (!isDecimalString(i) || compareDecimal(i, '0') < 0) return 'invalid';
  if (positive(qty) && compareDecimal(i, qty) > 0) return 'exceeds_quantity';
  return null;
}

export type CloseError = 'qty_invalid' | 'no_loan_selected' | 'qty_below_principal' | 'interest_invalid' | 'principal_mismatch' | null;

export interface CloseMath {
  principal: string | null;
  interest: string | null;
  error: CloseError;
}

/**
 * "Teljes lezárás": the principal part IS the sum of the selected loans' outstanding; the rest of the entered
 * quantity is the interest part. `interestInput` null = untouched (the rest is the interest, prefilled); a typed
 * interest is kept and then principal (quantity - interest) must equal the selected outstanding.
 */
export function closeMath(qty: string, selectedOutstanding: string, selectedCount: number, interestInput: string | null): CloseMath {
  if (!positive(qty)) return { principal: null, interest: null, error: 'qty_invalid' };
  if (selectedCount === 0) return { principal: null, interest: null, error: 'no_loan_selected' };
  if (interestInput === null) {
    const rest = subDecimal(qty, selectedOutstanding);
    if (compareDecimal(rest, '0') < 0) return { principal: selectedOutstanding, interest: null, error: 'qty_below_principal' };
    return { principal: selectedOutstanding, interest: rest, error: null };
  }
  if (interestError(qty, interestInput) !== null) return { principal: null, interest: null, error: 'interest_invalid' };
  const interest = interestInput.trim() === '' ? '0' : interestInput.trim().replace(',', '.');
  const principal = subDecimal(qty, interest);
  return { principal, interest, error: compareDecimal(principal, selectedOutstanding) === 0 ? null : 'principal_mismatch' };
}

/** Expected true-up (D48): interest - accrued interest (signed). Only meaningful on an accruing location when the repay closes the loan. */
export function expectedTrueUp(interest: string, accrued: string): string {
  return subDecimal(interest, accrued);
}
